import { Alert, Button, Modal } from "antd";
import { DELETE_TYPE } from "@zhihaoscm/meta";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { subManagementService } from "@/api/subManagementService";
import { Breadcrumb, DetailHeader, DetailJson, type DetailJsonBlock } from "@/components/common";
import { AccountType, useAccountSession } from "@/stores/session";
import { hasSubAccountPermission } from "./SubAccountManagementPage";
import { SubAccountPermissionDialog } from "./SubAccountPermissionDialog";

interface Detail {
    account?: { realName?: string; mobile?: string };
    customerEnterprise?: { id?: number; accountState?: number; receiptSms?: number };
}

/** 子账号详情兼容主账号带 id 查看和子账号查看自身信息两种入口。 */
export function SubAccountDetailPage() {
    const [detail, setDetail] = useState<Detail>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [permissionOpen, setPermissionOpen] = useState(false);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const accountType = useAccountSession(state => state.accountType);
    const id = Number(searchParams.get("id") || 0);
    const isSelfQuery = !id;
    const accountIsSub = accountType === AccountType.SUB_ACCOUNT;

    /** 有 id 时查询指定子账号，无 id 时查询当前子账号自身详情。 */
    const load = async () => {
        setLoading(true);
        setError(false);
        try {
            setDetail(
                id
                    ? ((await subManagementService.getSubAccountDetail(id)) as any)
                    : ((await subManagementService.getSubAccountDetailOnSub()) as any)
            );
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        void load();
    }, [id]);
    /** 编辑前校验会员子账号权益。 */
    const edit = () => {
        if (!hasSubAccountPermission()) {
            setPermissionOpen(true);
            return;
        }
        navigate(`/me/subAccountManagement/edit?id=${detail.customerEnterprise?.id}`);
    };
    /** 旧页删除成功后按浏览历史返回，保留调用方进入详情前的筛选状态。 */
    const remove = () =>
        Modal.confirm({
            title: "提示",
            content: "是否删除？删除后此信息不再展示",
            okText: "确定",
            cancelText: "取消",
            onOk: async () => {
                await subManagementService.deleteSubAccount(
                    detail.customerEnterprise!.id!,
                    DELETE_TYPE.DELETE_ACCOUNT.code
                );
                navigate(-1);
            }
        });

    // 旧 Vue 详情首屏静默等待数据，不额外展示页面级加载框。
    if (loading) return null;
    if (error)
        return (
            <Alert
                type="error"
                showIcon
                message="子账号信息加载失败"
                action={<Button onClick={() => void load()}>重试</Button>}
            />
        );
    const blocks: DetailJsonBlock<Detail>[] = [
        {
            sectionTitle: <div className="sub-account-sub-title">{accountIsSub ? "基本信息" : "子账号信息"}</div>,
            list: [
                { key: "account.realName", label: "姓名" },
                { key: "account.mobile", label: "手机号" },
                { label: "是否接收短信", value: detail.customerEnterprise?.receiptSms ? "是" : "否" }
            ]
        }
    ];
    const routes = accountIsSub
        ? [
              { name: "账号管理", path: "/me" },
              { name: "子账号信息", path: "/me/subAccountManagement/detail" }
          ]
        : [
              { name: "账号管理", path: "/me" },
              { name: "子账号管理", path: "/me/subAccountManagement" },
              { name: "子账号详情", path: "/me/subAccountManagement/detail" }
          ];
    return (
        <section className="sub-account-detail-page detail-container">
            <DetailHeader
                title={accountIsSub ? "子账号信息" : "子账号详情"}
                state={{ text: detail.customerEnterprise?.accountState ? "已激活" : "已失效" }}
                menu={<Breadcrumb routes={routes} />}
            />
            <DetailJson columns={blocks} data={detail} />
            {isSelfQuery ? null : (
                <div className="sub-account-detail-actions">
                    <Button type="primary" onClick={edit}>
                        编辑
                    </Button>
                    <Button type="primary" onClick={remove}>
                        移除
                    </Button>
                    <Button type="primary" onClick={() => navigate(-1)}>
                        返回
                    </Button>
                </div>
            )}
            <SubAccountPermissionDialog open={permissionOpen} onClose={() => setPermissionOpen(false)} />
        </section>
    );
}
